import React, { useState } from "react";
import { Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { WHATSAPP_URL } from "@/i18n/translations";

const Pricing = () => {
  const { t } = useLang();
  const [monthly, setMonthly] = useState(false);
  const scrollLead = () => {
    const el = document.getElementById("lead");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const co = t.pricing.cloudOps;
  const ar = t.pricing.aiRouter;

  const renderAddon = (addon, testId) => (
    <div
      data-testid={testId}
      className="rounded-2xl border border-sand-soft bg-maif-2 p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center"
    >
      <div className="md:col-span-4">
        <div className="eyebrow">{addon.subtitle}</div>
        <div className="mt-2 font-display text-2xl text-maif">{addon.name}</div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-display text-3xl text-maif">
            {monthly ? addon.priceMonthly : addon.price}
          </span>
          <span className="text-xs text-maif-muted">
            {monthly ? addon.cadenceMonthly : addon.cadence}
          </span>
        </div>
      </div>
      <ul className="md:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        {addon.features.map((f, k) => (
          <li key={k} className="flex items-start gap-2 text-maif">
            <Check size={14} className="mt-1 text-terracotta flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="md:col-span-2 flex md:justify-end">
        <button
          onClick={scrollLead}
          data-testid={`${testId}-cta`}
          className="btn-secondary"
        >
          {addon.cta}
        </button>
      </div>
    </div>
  );

  return (
    <section id="pricing" data-testid="pricing-section" className="py-16 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="eyebrow">{t.pricing.eyebrow}</div>
            <h2 className="mt-4 font-display font-light text-4xl md:text-5xl tracking-tight text-maif">
              {t.pricing.title}
            </h2>
          </div>

          <div
            data-testid="pricing-toggle"
            className="inline-flex items-center rounded-full border border-sand-soft p-1 bg-maif-2"
          >
            <button
              onClick={() => setMonthly(false)}
              data-testid="pricing-toggle-onetime"
              className={`px-4 py-2 rounded-full text-xs font-medium transition ${
                !monthly ? "bg-terracotta text-white" : "text-maif-muted"
              }`}
            >
              {t.pricing.toggle.oneTime}
            </button>
            <button
              onClick={() => setMonthly(true)}
              data-testid="pricing-toggle-monthly"
              className={`px-4 py-2 rounded-full text-xs font-medium transition ${
                monthly ? "bg-terracotta text-white" : "text-maif-muted"
              }`}
            >
              {t.pricing.toggle.monthly}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {t.pricing.tiers.map((tier, i) => {
            const highlight = !!tier.highlight;
            const price = monthly ? tier.priceMonthly : tier.price;
            const cadence = monthly ? tier.cadenceMonthly : tier.cadence;
            return (
              <div
                key={i}
                data-testid={`pricing-tier-${i}`}
                className={`relative rounded-2xl p-8 md:p-10 flex flex-col ${
                  highlight
                    ? "bg-[#111114] border border-terracotta glow-terracotta md:-translate-y-4"
                    : "bg-[#0E0E10] border border-sand-soft hover:border-sand-mid transition"
                }`}
              >
                {highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-terracotta text-white text-[10px] font-semibold uppercase tracking-[0.2em]">
                    ★ {tier.subtitle}
                  </span>
                )}
                <div className="eyebrow">{tier.name}</div>
                {!highlight && tier.subtitle && (
                  <div className="text-xs text-maif-muted mt-1">{tier.subtitle}</div>
                )}
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display font-light text-5xl md:text-6xl text-maif tracking-tighter">
                    {price}
                  </span>
                </div>
                <div className="mt-1 text-xs text-maif-muted">{cadence}</div>
                <p className="mt-4 text-maif-muted text-sm">{tier.desc}</p>

                <ul className="mt-7 space-y-3 flex-1">
                  {tier.features.map((f, k) => (
                    <li key={k} className="flex items-start gap-3 text-sm text-maif">
                      <Check size={16} className="mt-0.5 text-terracotta flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 flex flex-col gap-3">
                  <button
                    onClick={scrollLead}
                    data-testid={`pricing-cta-${i}`}
                    className={
                      highlight
                        ? "btn-primary justify-center w-full"
                        : "btn-secondary justify-center w-full"
                    }
                  >
                    {tier.cta}
                  </button>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-center text-xs text-maif-muted hover:text-maif transition"
                  >
                    or chat on WhatsApp →
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add-on bands: CloudOps + AI Router */}
        <div className="mt-10 space-y-4">
          {renderAddon(co, "cloudops-card")}
          {renderAddon(ar, "ai-router-card")}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
